/**
 * Grouping, filtering and moving the photos on a quote request (HQ #69).
 *
 * The Photos section shows a request's photos grouped by category, each group
 * in `imageOrder`. Dragging a photo can move it within its group or into
 * another one, so a move changes up to two things per row: its position and
 * its category. Everything here is pure — no React, no Appwrite — so those
 * rules can be tested without rendering a drag.
 *
 * A move renumbers `imageOrder` across the whole request in display order, so
 * the stored order is the order the section shows, and the viewer, the Damage
 * section and Panel Quote all see photos in the same sequence.
 */

export const DAMAGE_CATEGORY = 'damage';
export const OTHER_CATEGORY = 'extra';

// The groups the section always shows, even when empty: they mirror the two
// areas of the Add photos dialog, and an empty group is still somewhere to drag
// a photo to. Any other category (a Panel Quote import carries Panel Quote's
// own names) gets a group only while it holds photos.
export const FIXED_CATEGORIES = [DAMAGE_CATEGORY, OTHER_CATEGORY];

export const photoId = (image) => image.$id || image.id;

/** A photo with no category is shown, and treated, as "Other". */
export const categoryOf = (image) => image.imageCategory || OTHER_CATEGORY;

const byStoredOrder = (a, b) =>
  (a.imageOrder ?? 0) - (b.imageOrder ?? 0)
  || String(a.$createdAt || '').localeCompare(String(b.$createdAt || ''));

/**
 * Whether a photo record — or a File about to become one — is a PDF.
 *
 * A PDF can be attached to a request as a reference document, but it is not a
 * picture: nothing can preview it as one, the viewer cannot draw on it, and the
 * AI pipeline must never be handed it. Mirrors isPdfFile in the orchestrator's
 * pipeline-core.js.
 */
export function isPdfFile(item) {
  if (!item) return false;
  const type = String(item.mimeType || item.type || '').toLowerCase();
  if (type === 'application/pdf') return true;
  return /\.pdf$/i.test(String(item.filename || item.name || ''));
}

/** Categories in display order: the fixed pair, then any others alphabetically. */
export function photoCategories(images) {
  const others = [...new Set(images.map(categoryOf))]
    .filter((category) => !FIXED_CATEGORIES.includes(category))
    .sort((a, b) => a.localeCompare(b));
  return [...FIXED_CATEGORIES, ...others];
}

/** `[{ category, images }]` in display order, each group in stored order. */
export function groupPhotos(images, categories = photoCategories(images)) {
  return categories.map((category) => ({
    category,
    images: images.filter((image) => categoryOf(image) === category).sort(byStoredOrder),
  }));
}

/** Whether a photo passes the section's category and section (zone) filters. */
export function matchesPhotoFilter(image, { category = null, zone = null } = {}) {
  if (category && categoryOf(image) !== category) return false;
  if (zone && (image.zone || '') !== zone) return false;
  return true;
}

/** The sections (zones) that hold at least one photo. */
export function zonesWithPhotos(images) {
  return new Set(images.map((image) => image.zone).filter(Boolean));
}

/**
 * Move one photo and renumber the request.
 *
 * `targetIds` is the target group's visible ids after the drop, with the moved
 * photo in its new place. The section may be filtered, so `targetIds` can be a
 * subset of the group: the photo is placed immediately before the next visible
 * photo (or after the previous one, or at the end of the group), which leaves
 * every hidden photo where it was.
 *
 * Returns every photo in display order with `imageOrder` renumbered from 0 and
 * the moved photo's `imageCategory` set to `toCategory`. Unknown `activeId`
 * returns the photos unchanged.
 */
export function movePhoto(images, activeId, toCategory, targetIds) {
  const moving = images.find((image) => photoId(image) === activeId);
  if (!moving) return images;

  const rest = images.filter((image) => photoId(image) !== activeId);
  const categories = photoCategories([...rest, { imageCategory: toCategory }]);
  const groups = groupPhotos(rest, categories);
  const target = groups.find((group) => group.category === toCategory).images;

  const inTarget = (id) => target.some((image) => photoId(image) === id);
  const at = targetIds.indexOf(activeId);
  const nextId = at >= 0 ? targetIds.slice(at + 1).find(inTarget) : undefined;
  const prevId = at > 0 ? targetIds.slice(0, at).reverse().find(inTarget) : undefined;

  let insertAt = target.length;
  if (nextId) insertAt = target.findIndex((image) => photoId(image) === nextId);
  else if (prevId) insertAt = target.findIndex((image) => photoId(image) === prevId) + 1;
  target.splice(insertAt, 0, { ...moving, imageCategory: toCategory });

  return groups
    .flatMap((group) => group.images)
    .map((image, index) => ({ ...image, imageOrder: index }));
}

/**
 * The rows a move changed, as updates: `[{ id, data }]`.
 *
 * Compared against the photos as they were *before* the move. Comparing the
 * renumbered list against itself is always equal — that is exactly why the
 * order never saved before HQ #69: every photo's new `imageOrder` was checked
 * against the index it had just been given, so no write was ever sent.
 */
export function photoChanges(before, after) {
  const stored = new Map(before.map((image) => [photoId(image), image]));
  return after.flatMap((image) => {
    const was = stored.get(photoId(image));
    if (!was) return [];
    const data = {};
    if (was.imageOrder !== image.imageOrder) data.imageOrder = image.imageOrder;
    if (categoryOf(was) !== categoryOf(image)) data.imageCategory = image.imageCategory;
    return Object.keys(data).length > 0 ? [{ id: photoId(image), data }] : [];
  });
}
